// @file Vue mixin that handles keyboard traps for modals
// and keeps focus within the modal dialog
import { Component, Vue } from 'bits/vue'

@Component({})
export default class extends Vue {
  focusedElementBeforeModal: Element | null = null
  modalNodes: NodeList | Node[] = []
  isModalOpen = false

  openModal(modalNodes: NodeList | Node[]): void {
    // Save current focus
    this.focusedElementBeforeModal = document.activeElement
    this.modalNodes = modalNodes
    // Make sure to not have the modal completely hidden from the DOM tree
    const firstTabElement = this.modalNodes[0] as HTMLElement
    // Listen for keydown events and trap the keyboard
    document.addEventListener('keydown', this.trapKeys)

    // Show the modal and overlay
    // Toggled 'show' class (on the modal and overlay) depending on this property
    this.isModalOpen = true
    this.$nextTick(() => {
      // Focus first child
      firstTabElement.focus()
    })
  }

  trapKeys(e: KeyboardEvent): void {
    const firstTabStop = this.modalNodes[0] as HTMLElement
    const lastTabStop = this.modalNodes[this.modalNodes.length - 1] as HTMLElement
    // Check for certain keys
    const isTabPressed = e.key === 'Tab' || e.code === 'Tab' || e.keyCode === 9
    const isEscapePressed = e.key === 'Escape' || e.code === 'Escape' || e.keyCode === 27
    const isShiftPressed = e.shiftKey

    if (isEscapePressed) {
      this.closeModal()
    }

    if (isTabPressed && isShiftPressed) {
      // SHIFT + TAB
      if (document.activeElement === firstTabStop) {
        e.preventDefault()
        lastTabStop.focus()
      }
    } else if (isTabPressed) {
      // ONLY TAB
      if (document.activeElement === lastTabStop) {
        e.preventDefault()
        firstTabStop.focus()
      }
    }
  }

  closeModal(): void {
    // Hide the modal and overlay
    this.isModalOpen = false

    // Set focus back to element that had it before the modal was opened
    if (this.focusedElementBeforeModal instanceof HTMLElement) {
      this.focusedElementBeforeModal.focus()
    }
    document.removeEventListener('keydown', this.trapKeys)
  }

  beforeDestroy(): void {
    document.removeEventListener('keydown', this.trapKeys)
  }
}
